import React, { useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import { FormHelperText } from "@mui/material";
import _get from "lodash/get";
import { TimePicker } from "@mui/x-date-pickers";
import { useForm } from "@/src/provider/Form/FormProvider";
import { formValue } from "@/src/services/formServiceFunctions";
import { translate, translateHelperTextIfExists } from "@/src/services/translations/translations";
import Grid from "@mui/material/Grid";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { ValidatorType } from "@/src/types/ValidatorType";
import { requiredValidator, timeFieldDefault } from "@/src/services/validationRules";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import dayjs from "dayjs";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";

type TimeFieldConfiguredProps = {
  caseData: Record<string, any>;
  fieldPath: string;
  required?: boolean;
};

export default function TimeFieldConfigured({ caseData, fieldPath, required = false }: TimeFieldConfiguredProps) {
  const { values, errors, setError, handleChange, registerValidators }: FormDataTypes = useForm();

  const validators: Array<ValidatorType> = [timeFieldDefault];
  if (required) {
    validators.push(requiredValidator());
  }

  const error = !!_get(errors, fieldPath);
  const helperText = translateHelperTextIfExists(caseData, fieldPath);
  const question = translate(`${caseData.name}.${fieldPath}.question`, caseData, "");
  const label = translate(`${caseData.name}.${fieldPath}.label`, caseData);

  useEffect(() => {
    registerValidators(fieldPath, validators);
    return () => {
      registerValidators(fieldPath, []);
      setError(fieldPath, undefined);
    };
  }, [fieldPath]);

  const currentFormValue = formValue(values, fieldPath);

  return (
    <>
      {question && (
        <Grid size={12}>
          <FormLegend>{question}</FormLegend>
        </Grid>
      )}
      <Grid size={12}>
        <FormControl fullWidth error={error} sx={{ margin: "1rem 0 .3rem 0" }}>
          <TimePicker
            value={currentFormValue ? dayjs(currentFormValue) : null}
            onChange={(time) => {
              handleChange({ target: { name: fieldPath, value: time } });
            }}
            label={label}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            slotProps={{
              textField: {
                name: fieldPath,
                error: error,
                slotProps: {
                  htmlInput: { "aria-required": required },
                  inputLabel: { required },
                },
              },
            }}
          />
          <FormHelperText sx={error ? { color: "error.main" } : {}}>
            {_get(errors, fieldPath) || helperText}
          </FormHelperText>
        </FormControl>
      </Grid>
    </>
  );
}
